// ── Private "Family Videos" page ─────────────────────────────────────────
// Visible only to the admin and to emails listed in familyAccess/config.
// Video list lives in Firestore (collection "familyVideos"), NOT in the JS bundle,
// so the links are never exposed publicly. Protected by firestore.rules.
import React, { useEffect, useState, useRef } from "react";
import { db } from "./firebase";
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc, writeBatch } from "firebase/firestore";

const ytId = (s = "") => {
  const m = s.match(/(?:embed\/|watch\?v=|youtu\.be\/|shorts\/)([\w-]{11})/) || s.match(/^([\w-]{11})$/);
  return m ? m[1] : null;
};
const GRADES = ["Std 12", "Std 11", "IB Physics"];
const gradeOf = v => v.grade || "Std 12";
// IB Physics members-only videos (🔒 in title, or MCQ sets except the free Kinematics one) open on YouTube instead of playing here
const needsYT = v => gradeOf(v) === "IB Physics" && (/🔒/.test(v.title || "") || (/^MCQ\s/.test(v.title || "") && v.youtubeId !== "HBgcJhirhu8"));
const ytUrl = v => `https://www.youtube.com/watch?v=${v.youtubeId}`;
// ── Topic search: every word typed must match the title or a topic keyword ──
const tokensOf = q => q.toLowerCase().split(/[\s,]+/).map(t => t.trim()).filter(Boolean).map(t => (t.length > 4 && /[^s]s$/.test(t)) ? t.slice(0, -1) : t);
const escRe = t => t.replace(/[.*+?^$()|[\]\\{}]/g, "\\$&");
const tokenHits = (tok, text) => tok.length <= 3
  ? new RegExp("(^|[^a-z0-9])" + escRe(tok) + "([^a-z0-9]|$)").test(text)
  : text.includes(tok);
const matchVideo = (v, toks) => {
  const kws = (v.keywords || []).map(k => k.toLowerCase());
  const hay = (v.title || "").toLowerCase() + " | " + kws.join(" | ");
  if (!toks.every(t => tokenHits(t, hay))) return null;
  return kws.filter(k => toks.some(t => tokenHits(t, k))).slice(0, 5);
};
const fmtT = sec => { const h = Math.floor(sec / 3600), m = Math.floor(sec % 3600 / 60), x = sec % 60; return (h ? h + ":" + String(m).padStart(2, "0") : m) + ":" + String(x).padStart(2, "0"); };
const classNo = t => { const m = (t || "").match(/Class\s*(\d+)/i); return m ? `Class ${m[1]}` : ""; };

const WATCHED_KEY = "familyVideosWatched";
const loadWatched = () => { try { return JSON.parse(localStorage.getItem(WATCHED_KEY) || "[]"); } catch { return []; } };
const saveWatched = (ids) => { try { localStorage.setItem(WATCHED_KEY, JSON.stringify(ids)); } catch {} };

export default function FamilyVideos({ user, isAdmin }) {
  const [videos, setVideos] = useState([]);
  const [emails, setEmails] = useState([]);
  const [current, setCurrent] = useState(null);
  const [search, setSearch] = useState("");
  const [watched, setWatched] = useState(loadWatched());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showManage, setShowManage] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [grade, setGrade] = useState("Std 12");
  const [kwText, setKwText] = useState("");
  const [startAt, setStartAt] = useState(0);
  const frameRef = useRef(null);
  const pendingSeek = useRef(null);
  // Talk to the YouTube player (IFrame API via postMessage) — seek + play without reloading
  const ytCmd = (func, args = []) => {
    const w = frameRef.current && frameRef.current.contentWindow;
    if (w) w.postMessage(JSON.stringify({ event: "command", func, args }), "*");
  };
  const seekPlay = t => { ytCmd("seekTo", [t, true]); ytCmd("playVideo"); };
  // Has the viewer pressed play at least once? (Safari blocks remote play until then)
  const startedRef = useRef(false);
  const [frameNonce, setFrameNonce] = useState(0);
  useEffect(() => {
    const onMsg = e => {
      if (!/youtube/.test(String(e.origin))) return;
      try { const d = JSON.parse(e.data); const st = d && d.info && d.info.playerState; if (st === 1 || st === 2 || st === 3) startedRef.current = true; } catch {}
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  const onFrameLoad = () => {
    const w = frameRef.current && frameRef.current.contentWindow;
    if (w) w.postMessage(JSON.stringify({ event: "listening", id: 1 }), "*");
    startedRef.current = false;
  };

  const load = async () => {
    setLoading(true); setError("");
    try {
      const snap = await getDocs(collection(db, "familyVideos"));
      const list = snap.docs.map(d => {
        // Topics saved as "name@seconds" (older import) → split into keywords + topicTimes
        const v = { id: d.id, ...d.data() };
        if (Array.isArray(v.keywords) && v.keywords.some(k => /@\d+$/.test(k))) {
          const times = { ...(v.topicTimes || {}) };
          v.keywords = v.keywords.map(k => { const m = k.match(/^(.*?)@(\d+)$/); if (!m) return k; times[m[1].trim()] = Number(m[2]); return m[1].trim(); });
          v.topicTimes = times;
        }
        return v;
      }).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setVideos(list);
      setCurrent(c => c && list.find(v => v.id === c.id) ? c : list.find(v => !needsYT(v)) || list[0] || null);
      if (isAdmin) {
        const acc = await getDoc(doc(db, "familyAccess", "config"));
        setEmails(acc.exists() ? (acc.data().emails || []) : []);
      }
    } catch (e) {
      setError("You don't have access to this page.");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const play = (v, start = 0) => {
    if (needsYT(v)) {
      window.open(ytUrl(v) + (start ? `&t=${start}s` : ""), "_blank", "noopener");
      if (!watched.includes(v.id)) { const w = [...watched, v.id]; setWatched(w); saveWatched(w); }
      return;
    }
    if (gradeOf(v) !== grade) setGrade(gradeOf(v));
    if (current && current.id === v.id) {
      if (!start) return;
      if (startedRef.current) { seekPlay(start); return; }          // already playing → jump instantly
      setStartAt(start); setFrameNonce(n => n + 1); return;          // not started yet → reload at that time
    }
    startedRef.current = false;
    setStartAt(start || 0);
    pendingSeek.current = null;
    setCurrent(v);
    if (!watched.includes(v.id)) { const w = [...watched, v.id]; setWatched(w); saveWatched(w); }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Admin actions ──
  const saveEmails = async (list) => {
    await setDoc(doc(db, "familyAccess", "config"), { emails: list, updatedAt: new Date().toISOString() });
    setEmails(list);
  };
  const addEmail = async () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || !e.includes("@")) return alert("Enter a valid email.");
    if (emails.includes(e)) return setNewEmail("");
    try { await saveEmails([...emails, e]); setNewEmail(""); } catch (err) { alert("Error: " + err.message); }
  };
  const removeEmail = async (e) => {
    if (!window.confirm(`Remove access for ${e}?`)) return;
    try { await saveEmails(emails.filter(x => x !== e)); } catch (err) { alert("Error: " + err.message); }
  };
  const bulkAdd = async () => {
    const lines = bulkText.split("\n").map(l => l.trim()).filter(Boolean);
    const items = [];
    const bad = [];
    lines.forEach(l => {
      // Format: Title | YouTube link | Worksheet PDF link (optional)
      const parts = l.split("|").map(p => p.trim()).filter(Boolean);
      const ytPart = parts.find(p => ytId(p) && /youtu|^[\w-]{11}$/.test(p));
      const pdfParts = parts.filter(p => /^https?:\/\/\S+\.pdf/i.test(p)).map(p => p.replace(/#.*$/, "").replace(/&amp;/g, "&"));
      const id = ytPart ? ytId(ytPart) : null;
      const title = parts.filter(p => p !== ytPart && !/^https?:\/\/\S+\.pdf/i.test(p)).join(" | ");
      if (id && title) items.push({ title, youtubeId: id, pdfUrl: pdfParts[0] || "", pdfUrls: pdfParts }); else bad.push(l);
    });
    if (items.length === 0) return alert("No valid lines. Use:  Title | YouTube link");
    setBusy(true);
    try {
      const start = videos.reduce((m, v) => Math.max(m, v.order ?? 0), -1) + 1;
      const byYt = Object.fromEntries(videos.map(v => [v.youtubeId, v]));
      const b = writeBatch(db);
      let n = 0, updated = 0;
      items.forEach((it) => {
        const ex = byYt[it.youtubeId];
        if (ex) {
          // Already in the list: just fill in / refresh the worksheet link and title
          b.update(doc(db, "familyVideos", ex.id), { title: it.title, ...(it.pdfUrl ? { pdfUrl: it.pdfUrl, pdfUrls: it.pdfUrls } : {}) });
          updated++;
          return;
        }
        b.set(doc(collection(db, "familyVideos")), { ...it, grade, order: start + n, addedAt: new Date().toISOString() });
        n++;
      });
      await b.commit();
      setBulkText("");
      alert(`✅ Added ${n} new video(s) to ${grade}${updated ? `, updated ${updated} existing` : ""}.${bad.length ? `\n⚠️ Could not read ${bad.length} line(s).` : ""}`);
      load();
    } catch (err) { alert("Error: " + err.message); }
    setBusy(false);
  };
  const importKeywords = async () => {
    const byYt = Object.fromEntries(videos.map(v => [v.youtubeId, v]));
    const lines = kwText.split("\n").map(l => l.trim()).filter(Boolean);
    let n = 0; const miss = [];
    setBusy(true);
    try {
      for (let i = 0; i < lines.length; i += 400) {
        const b = writeBatch(db);
        lines.slice(i, i + 400).forEach(l => {
          const [idPart, ...rest] = l.split("|");
          const id = ytId(idPart.trim()) || idPart.trim();
          const v = byYt[id];
          const raw = rest.join("|").split(",").map(k => k.trim()).filter(Boolean);
          const kws = [], times = {};
          raw.forEach(k => { const m = k.match(/^(.*?)@(\d+)$/); const name = (m ? m[1] : k).trim(); if (!name) return; kws.push(name); if (m) times[name] = Number(m[2]); });
          if (!v || !kws.length) { miss.push(idPart.trim()); return; }
          b.update(doc(db, "familyVideos", v.id), { keywords: kws, topicTimes: times }); n++;
        });
        await b.commit();
      }
      setKwText("");
      alert(`✅ Search topics saved for ${n} video(s).${miss.length ? `\n⚠️ ${miss.length} line(s) didn't match a video in the list.` : ""}`);
      load();
    } catch (err) { alert("Error: " + err.message); }
    setBusy(false);
  };
  const clearGrade = async () => {
    const list = videos.filter(v => gradeOf(v) === grade);
    if (!list.length) return;
    if (!window.confirm(`Delete ALL ${list.length} videos in "${grade}"? Other tabs are not touched.`)) return;
    setBusy(true);
    try {
      for (let i = 0; i < list.length; i += 400) {
        const b = writeBatch(db);
        list.slice(i, i + 400).forEach(v => b.delete(doc(db, "familyVideos", v.id)));
        await b.commit();
      }
      alert(`🗑 Deleted ${list.length} video(s) from ${grade}.`);
      load();
    } catch (err) { alert("Error: " + err.message); }
    setBusy(false);
  };
  const removeVideo = async (v) => {
    if (!window.confirm(`Remove "${v.title}"?`)) return;
    try { await deleteDoc(doc(db, "familyVideos", v.id)); load(); } catch (err) { alert("Error: " + err.message); }
  };

  const inGrade = videos.filter(v => gradeOf(v) === grade);
  const toks = tokensOf(search);
  const searching = toks.length > 0;
  const results = searching
    ? videos.map(v => ({ v, hits: matchVideo(v, toks) })).filter(r => r.hits !== null)
    : [];
  const shown = searching ? results.map(r => r.v) : inGrade;
  const hitsOf = Object.fromEntries(results.map(r => [r.v.id, r.hits]));
  const switchGrade = g => { setGrade(g); setSearch(""); setCurrent(videos.find(v => gradeOf(v) === g && !needsYT(v)) || videos.find(v => gradeOf(v) === g) || null); };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="fv-page"><p className="fv-empty">🔒 {error}</p></div>;

  return (
    <div className="fv-page">
      <div className="fv-head">
        <div>
          <h1>📺 Family Videos</h1>
          <p className="fv-sub">Private reference videos · visible only to you{isAdmin ? " and the accounts you allow" : ""}</p>
        </div>
        {isAdmin && <button className="fv-btn" onClick={() => setShowManage(!showManage)}>{showManage ? "✕ Close" : "⚙️ Manage"}</button>}
      </div>

      <div className="fv-tabs">
        {GRADES.map(g => (
          <button key={g} className={grade === g ? "active" : ""} onClick={() => switchGrade(g)}>
            {/Physics/.test(g) ? g : `${g} Physics`} <span>{videos.filter(v => gradeOf(v) === g).length}</span>
          </button>
        ))}
      </div>

      {isAdmin && showManage && (
        <div className="fv-manage">
          <div className="fv-box">
            <h3>👤 Who can see this page</h3>
            <p className="fv-hint">Add your son's Google email. Only these accounts (and you) can open this page.</p>
            <div className="fv-row">
              <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="son@gmail.com" onKeyDown={e => e.key === "Enter" && addEmail()} />
              <button className="fv-btn primary" onClick={addEmail}>Add</button>
            </div>
            {emails.length === 0 ? <p className="fv-hint">No one added yet.</p> : (
              <ul className="fv-emails">{emails.map(e => <li key={e}>{e}<button onClick={() => removeEmail(e)}>✕</button></li>)}</ul>
            )}
          </div>
          <div className="fv-box">
            <h3>🔎 Search topics</h3>
            <p className="fv-hint">One per line: <code>YouTube ID or link | topic, topic, topic</code>. Replaces that video's topics. ({videos.filter(v => (v.keywords || []).length).length} of {videos.length} videos have topics)</p>
            <textarea value={kwText} onChange={e => setKwText(e.target.value)} rows={4} placeholder={"pSudi7v5xRg | ac circuits, resonance, impedance, phase angle"} />
            <button className="fv-btn primary" disabled={busy || !kwText.trim()} onClick={importKeywords}>{busy ? "Saving…" : "Save topics"}</button>
          </div>
          <div className="fv-box">
            <h3>➕ Add videos to <span style={{ color: "#f5c518" }}>{grade}</span></h3>
            <p className="fv-hint">Switch the Std 11 / Std 12 / IB Physics tab above to choose where new videos go.</p>
            <p className="fv-hint">One per line: <code>Title | YouTube link | Worksheet PDF link</code> (worksheet optional; add more PDF links with extra <code>|</code>). Pasting a video that's already here updates it.</p>
            <textarea value={bulkText} onChange={e => setBulkText(e.target.value)} rows={6} placeholder={"Class 01 Electrostatics | https://www.youtube.com/watch?v=XXXXXXXXXXX"} />
            <button className="fv-btn primary" disabled={busy} onClick={bulkAdd}>{busy ? "Adding…" : "Add videos"}</button>
            <button className="fv-btn" style={{ marginLeft: 8, borderColor: "#c0392b", color: "#e74c3c" }} disabled={busy || !inGrade.length} onClick={clearGrade}>🗑 Delete all {inGrade.length} in {grade}</button>
          </div>
        </div>
      )}

      {inGrade.length === 0 ? (
        <p className="fv-empty">No {grade} videos yet.{isAdmin ? " Click ⚙️ Manage to add them." : ""}</p>
      ) : (
        <div className="fv-layout">
          <div className="fv-player">
            {current && (<>
              <div className="fv-frame">
                <iframe ref={frameRef} onLoad={onFrameLoad} key={current.youtubeId + "_" + frameNonce} src={`https://www.${gradeOf(current) === "IB Physics" ? "youtube.com" : "youtube-nocookie.com"}/embed/${current.youtubeId}?rel=0&enablejsapi=1&playsinline=1&origin=${encodeURIComponent(window.location.origin)}${startAt ? `&start=${startAt}&autoplay=1` : ""}`} title={current.title} allow="autoplay; accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen />
              </div>
              <h2>{current.title}</h2>
              {(current.keywords || []).length > 0 && <div className="fv-topics">{current.keywords.map(k => {
                const t = current.topicTimes && current.topicTimes[k];
                return t !== undefined
                  ? <button key={k} className="timed" title={`Jump to ${fmtT(t)}`} onClick={() => play(current, t)}>⏱ {k} <small>{fmtT(t)}</small></button>
                  : <button key={k} title="Search other lectures" onClick={() => setSearch(k)}>{k}</button>;
              })}</div>}
              {(() => { const ws = (current.pdfUrls && current.pdfUrls.length) ? current.pdfUrls : (current.pdfUrl ? [current.pdfUrl] : []);
                return ws.length === 0 ? <p className="fv-hint">No worksheet for this class.</p> : (
                  <div className="fv-ws-row">{ws.map((u, i) => (
                    <a key={u} className="fv-btn primary fv-ws" href={u} target="_blank" rel="noreferrer">📄 {ws.length > 1 ? `Worksheet ${i + 1}` : "Open worksheet (PDF)"}</a>
                  ))}</div>); })()}
              <a className="fv-btn fv-ws" href={`https://www.youtube.com/watch?v=${current.youtubeId}`} target="_blank" rel="noreferrer">▶ Open on YouTube{grade === "IB Physics" ? " (members-only videos play here)" : ""}</a>
              <p className="fv-sub">{inGrade.filter(v => watched.includes(v.id)).length} of {inGrade.length} watched on this device</p>
            </>)}
          </div>
          <div className="fv-list">
            <input type="search" autoComplete="off" spellCheck={false} className="fv-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search topics: AC, resonance, angle, Gauss…" />
            {searching && <p className="fv-hint" style={{ margin: "6px 2px" }}>{shown.length ? `${shown.length} lecture${shown.length > 1 ? "s" : ""} found (all tabs)` : "No lecture found. Try another word."} <button className="fv-clear" onClick={() => setSearch("")}>✕ Clear</button></p>}
            <ul>
              {shown.map(v => (
                <li key={v.id} className={`${current?.id === v.id ? "active" : ""}`} onClick={() => { const hit = searching && (hitsOf[v.id] || []).map(h => (v.topicTimes || {})[(v.keywords || []).find(k => k.toLowerCase() === h)]).find(t => t !== undefined); play(v, hit || 0); }}>
                  <img src={`https://i.ytimg.com/vi/${v.youtubeId}/mqdefault.jpg`} alt="" loading="lazy" />
                  <span className="fv-title">
                    {searching && <span className="fv-grade">{gradeOf(v)} · {classNo(v.title)}</span>}
                    {watched.includes(v.id) && <span className="fv-tick">✓ </span>}{v.title}{v.pdfUrl && <span className="fv-pdf"> · 📄</span>}{needsYT(v) && <span className="fv-pdf" style={{ color: "#ff6b6b" }}> · ↗ YouTube</span>}
                    {searching && (hitsOf[v.id] || []).length > 0 && <span className="fv-hits">{hitsOf[v.id].map(k => { const orig = (v.keywords || []).find(x => x.toLowerCase() === k); const t = orig && (v.topicTimes || {})[orig]; return <em key={k}>{t !== undefined && t !== null ? "⏱ " : ""}{k}{t !== undefined && t !== null ? " " + fmtT(t) : ""}</em>; })}</span>}
                  </span>
                  {isAdmin && showManage && <button className="fv-del" onClick={e => { e.stopPropagation(); removeVideo(v); }}>🗑</button>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
